import { useState, useEffect, createRef } from 'react';
import Page from '..';
import Form from '../../Form';
import useStore from '../../Hooks/useStore';
import './Banner.css';
import Tabs from '../../Tabs';
import { PageBannerFiles } from './Files';
import SVG from '../../SVG';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PageBannerPromotions } from './Promotions';
import { PageBannerLayouts } from './Layouts';
import { PageBannerConfigurations } from './Configurations';
import * as lodash from 'lodash';
import Tag from '../../Tag';

function PageBanner() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [getters, setters] = useStore();
  const {
    form: {
      getForm,
      getTransformations,
      getBannerConfigurations: getConfig,
      getShowSingleForm,
    },
    banner: { getBanner, getDefaultBannerForm, getBackgroundImage },
  } = getters;
  const {
    modal: { openSingleModal },
    banner: {
      sendFormBanner,
      cancelEditFormBanner,
      setBannerConfigs,
      updateUserSiganature,
      setBanner,
      addSingleBannerFormsToPrint,
    },
    form: { setForm },
  } = setters;
  const [screenHeight, setScreenHeight] = useState(window.innerHeight);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const [createLoading, setCreatingLoading] = useState(false);
  const form = getShowSingleForm(null);

  const updateScreen = () => {
    setScreenHeight(window.innerHeight);
    setScreenWidth(window.innerWidth);
  };
  const ref = createRef<HTMLDivElement>();

  useEffect(() => {
    window.addEventListener('resize', updateScreen);
    return () => {
      window.removeEventListener('resize', updateScreen);
    };
  }, []);

  const orientation = form.model.orientation;
  const size = form.model.size;

  function aspect() {
    switch (orientation) {
      case 'landscape':
        return ((297 / 210) * 0.6) / (size === 'EP21' ? 0.9 : 1);
      case 'portrait':
        return (1 * 210) / 297;
      default:
        return (297 / 210) * 0.6;
    }
  }

  const margin = 0.02 * screenHeight;
  const previewHeaderHeight = 0.06 * screenHeight;
  const previewWidth = ((0.88 - 0.06) * screenHeight - 15) * aspect();
  const previewHeight = 0.88 * screenHeight;

  const elements = getTransformations(previewWidth, form);
  const { height } = getConfig(previewWidth, form);
  const backGroundImageUrl = getBackgroundImage(form);

  const onPrintBanner = () => {
    updateUserSiganature();
    addSingleBannerFormsToPrint([getForm('batchBanner')]);
    openSingleModal('printBannerForm');
  };

  const onSaveBanner = () => {
    updateUserSiganature();
    const isEditMode = getBanner('isEditMode');
    setCreatingLoading(true);
    sendFormBanner()
      .then(() => {
        enqueueSnackbar(
          `Cartaz ${isEditMode ? 'atualizado' : 'criado'} com sucesso`,
          { variant: 'success' },
        );
        setCreatingLoading(false);
      })
      .catch(() => {
        enqueueSnackbar(
          `Houve uma falha na ${
            isEditMode ? 'atualização' : 'criação'
          } do cartaz`,
          {
            variant: 'error',
          },
        );
        setCreatingLoading(false);
      });
  };

  const onClearBanner = () => {
    const batchBanner = getForm('batchBanner');
    setForm('batchBanner', {
      ...batchBanner,
      singleForms: [],
      batch: {
        ...batchBanner.batch,
        productType: 'normal',
        pinnedSingleForms: [],
      },
      filters: {
        ...batchBanner.filters,
        products: {
          ...batchBanner.filters.products,
          filter: [],
        },
      },
      showSingleFormId: 0,
      defaultSingleForm: getDefaultBannerForm(),
      autocompletes: {
        ...batchBanner.autocompletes,
        promotion: {
          ...batchBanner.autocompletes.promotion,
          value: '',
          filter: [],
        },
        type: {
          ...batchBanner.autocompletes.type,
          value: '',
        },
      },
    });

    setBannerConfigs();
    updateUserSiganature();
  };

  const onCancelEditFormBanner = () => {
    cancelEditFormBanner().then(() => {
      navigate(`/tools/banner/files`);
    });
  };
  const {
    batch: { pinnedSingleForms },
    singleForms,
  } = getForm('batchBanner');

  const hasPinnedSingleForms = pinnedSingleForms.length > 0;
  const hasSelectedProducts = singleForms.length > 0;

  const selectedProductsTagLabel = hasSelectedProducts
    ? `${singleForms.length} ${
        singleForms.length > 1 ? 'produtos selecionados' : 'produto selecionado'
      }`
    : 'Sem produtos selecionados';
  const pinnedSingleFormsTagLabel = `${pinnedSingleForms.length} ${pinnedSingleForms.length > 1 ? 'produtos fixados' : 'produto fixado'}  `;
  const tagLabel = hasPinnedSingleForms
    ? pinnedSingleFormsTagLabel
    : selectedProductsTagLabel;

  return (
    <Page className="page-banner">
      <div
        ref={ref}
        style={{
          position: 'relative',
          transition: 'min-width 500ms, max-width 500ms',
          minWidth: `${screenWidth - previewWidth - 5 * margin}px`,
          minHeight: `${previewHeight - 2 * margin - 15}px`,
          maxHeight: `${previewHeight - 2 * margin - 15}px`,
          margin: `0 ${margin}px`,
          padding: `${2 * margin}px ${margin}px ${0}px ${margin}px`,
        }}
        className="form-container"
      >
        <Tabs
          tab={getBanner('activeTab')}
          setTab={(value: number) => setBanner({ field: 'activeTab', value })}
          tags={hasSelectedProducts ? [<Tag>{tagLabel}</Tag>] : []}
          items={[
            {
              label: 'MODELO',
              children: <Form.Banner.Post className="form-banner-post" />,
            },
            {
              label: 'PRODUTO',
              children: <Form.Banner.Description />,
            },
            {
              label: 'PREÇO',
              children: <Form.Banner.Price />,
            },
            {
              label: 'LOTE',
              children: <Form.Banner.Batch />,
            },
          ]}
        >
          <div
            style={{
              display: 'flex',
              margin: '3px 0 3px auto',
              padding: '0 15px',
              justifyContent: 'center',
              alignItems: 'center',
              background: '#364856',
              borderRadius: '10px',
              border: '2px solid',
              columnGap: '15px',
            }}
          >
            {getBanner('isEditMode') && (
              <label
                style={{
                  cursor: 'pointer',
                }}
              >
                <SVG.Icon.SVGIconCloseSmall
                  width={30}
                  height={30}
                  onClick={onCancelEditFormBanner}
                />
              </label>
            )}

            <label
              style={{
                cursor: 'pointer',
              }}
            >
              {!createLoading ? (
                <SVG.Icon.SVGIconSaveAsh onClick={onSaveBanner} />
              ) : (
                <SVG.Icon.Loader width={30} height={30} color={'white'} />
              )}
            </label>

            <label
              style={{
                cursor: 'pointer',
              }}
            >
              <SVG.Icon.SVGIconGrayBroom onClick={onClearBanner} />
            </label>

            <label
              style={{
                cursor: 'pointer',
              }}
            >
              <SVG.Icon.SVGIconGrayPrinter onClick={onPrintBanner} />
            </label>
          </div>
        </Tabs>
      </div>
      <Form.Banner.Preview
        elements={elements}
        width={previewWidth}
        height={height}
        headerHeight={previewHeaderHeight}
        margin={margin}
        backGroundImageUrl={backGroundImageUrl}
      />
    </Page>
  );
}
export default Object.assign(PageBanner, {
  Files: PageBannerFiles,
  Promotions: PageBannerPromotions,
  Layouts: PageBannerLayouts,
  Configurations: PageBannerConfigurations,
});
